import { PLACE_ID_PATTERN } from '@bandido/shared/types';

import type {
  CarsSearchPayload,
  PricedSearchResult,
  ProvidersOutcome,
} from './queries';
import { PlaceRef } from './places';
import { routeMapPoints } from './routeMap';
import type { RouteMapPoint } from './routeMap';

/**
 * Helpers del vertical de autos.
 *
 * El proveedor publica todo en INGLÉS y como texto libre ("Large SUV",
 * "Unlimited Miles", "Automatic"): la traducción es responsabilidad del
 * frontend (misma decisión que en `destinations.ts`), token por token, con
 * fallback al texto original cuando no lo conocemos — nunca se oculta un dato
 * del proveedor por no tener traducción.
 */

/** Oferta de auto: campos propios del vertical sobre el resultado priceado. */
export type CarOffer = PricedSearchResult & {
  category?: string;
  carModel?: string;
  sippCode?: string;
  company?: string;
  /** URLs ya validadas por el backend (o ausentes si el proveedor mandó basura). */
  carImageUrl?: string;
  companyLogoUrl?: string;
  /** Plan de tarifa ("Just Car", "Basic Protection", "Full Protection"). */
  rateName?: string;
  rateDescription?: string;
  fareConditions?: { type: string; description: string; limit?: string }[];
  /** "On Terminal" (en el aeropuerto) o "Shuttle" (traslado a la oficina). */
  terminalAccess?: string;
  officeCode?: string;
};

export type CarDropOffReferenceStatus = 'absent' | 'canonical' | 'invalid';

function carPointReferenceStatus(
  params: Record<string, string>,
  prefix: 'pickUp' | 'dropOff',
): CarDropOffReferenceStatus {
  const code = params[`${prefix}Code`];
  const hasCoordinates =
    params[`${prefix}Lat`] !== undefined || params[`${prefix}Lng`] !== undefined;
  if (!code && !hasCoordinates) return 'absent';
  if (code && PLACE_ID_PATTERN.test(code) && !hasCoordinates) return 'canonical';
  return 'invalid';
}

export function carPickUpReferenceStatus(
  params: Record<string, string>,
): CarDropOffReferenceStatus {
  return carPointReferenceStatus(params, 'pickUp');
}

export function carDropOffReferenceStatus(
  params: Record<string, string>,
): CarDropOffReferenceStatus {
  return carPointReferenceStatus(params, 'dropOff');
}

/** Reconstruye Cars sin convertir una devolución inválida en retiro y devolución iguales. */
export function carSearchPayloadFromParams(
  params: Record<string, string>,
): CarsSearchPayload | null {
  if (params.buscar !== '1') return null;
  const { pickUpDate, dropOffDate, pais, residencia, pickUpCode } = params;
  if (
    !pickUpDate ||
    !dropOffDate ||
    !pais ||
    !residencia ||
    !pickUpCode ||
    carPickUpReferenceStatus(params) !== 'canonical'
  ) {
    return null;
  }

  const dropOffStatus = carDropOffReferenceStatus(params);
  if (dropOffStatus === 'invalid') return null;

  return {
    pickUpCode,
    ...(dropOffStatus === 'canonical' ? { dropOffCode: params.dropOffCode } : {}),
    pickUpDate,
    pickUpTime: params.pickUpTime ?? '10:00',
    dropOffDate,
    dropOffTime: params.dropOffTime ?? '10:00',
    country: pais,
    source: residencia,
    ...(params.pago === 'ppd' || params.pago === 'pod'
      ? { paymentType: params.pago }
      : {}),
  };
}

/**
 * Repite una búsqueda base contra el proveedor de autos que publica planes
 * alternativos. El spread preserva retiro, devolución, país, residencia y
 * forma de pago; solo agrega la dirección y `mode: all` del contrato.
 */
export function carRatePlanComparisonPayload(
  base: CarsSearchPayload,
): CarsSearchPayload {
  return {
    ...base,
    providers: ['agentcars'],
    ratePlans: { mode: 'all' },
  };
}

export const CAR_RATE_PLAN_PROVIDER = 'agentcars';

export function isCarRatePlanProvider(offer: CarOffer): boolean {
  return offer.provider.toLowerCase() === CAR_RATE_PLAN_PROVIDER;
}

/** La expansión dirigida falló aunque el envelope HTTP haya llegado bien. */
export function carRatePlanComparisonFailed(
  providers: ProvidersOutcome | undefined,
): boolean {
  if (!providers) return true;
  const matches = (provider: string) =>
    provider.toLowerCase() === CAR_RATE_PLAN_PROVIDER;
  const responded = providers.responded.some(matches);
  return (
    !responded ||
    providers.failed.some(({ provider }) => matches(provider)) ||
    providers.timedOut.some(matches) ||
    Boolean(providers.skipped?.some(({ provider }) => matches(provider)))
  );
}

export type CarRatePlanComparisonState =
  | 'idle'
  | 'loading'
  | 'completed'
  | 'failed';

export function carRatePlanComparisonState({
  requested,
  fetching,
  requestFailed,
  providers,
}: {
  requested: boolean;
  fetching: boolean;
  requestFailed: boolean;
  providers: ProvidersOutcome | undefined;
}): CarRatePlanComparisonState {
  if (!requested) return 'idle';
  if (fetching || (!providers && !requestFailed)) return 'loading';
  if (requestFailed || carRatePlanComparisonFailed(providers)) return 'failed';
  return 'completed';
}

/** Identidad no cacheable de una comparación explícita y su generación base. */
export function carRatePlanComparisonGenerationKey(
  baseUpdatedAt: number,
  requestId: string,
): readonly [number, string] {
  return [baseUpdatedAt, requestId];
}

/**
 * Una expansión exitosa reemplaza —no acumula— la generación `best` de
 * AgentCars. Los demás proveedores permanecen exactamente como estaban y una
 * expansión válida sin inventario elimina la tarifa `best` anterior.
 */
export function mergeCarRatePlanComparison(
  base: CarOffer[],
  expanded: CarOffer[],
  completed: boolean,
): CarOffer[] {
  if (!completed) return base;
  return [
    ...base.filter((offer) => !isCarRatePlanProvider(offer)),
    ...expanded.filter(isCarRatePlanProvider),
  ];
}

export function carRatePlanNames(offers: CarOffer[]): string[] {
  return [...new Set(
    offers
      .filter(isCarRatePlanProvider)
      .map((offer) => offer.rateName?.trim())
      .filter((name): name is string => Boolean(name)),
  )].sort((a, b) => a.localeCompare(b));
}

/**
 * Un vehículo con sus PLANES de tarifa. El proveedor devuelve una oferta por
 * combinación auto × cobertura (el mismo Tahoe sale 5 veces entre USD 303 y
 * 765), así que se agrupan: el asesor elige primero el auto y después qué
 * cobertura vende — mismo criterio que las tarifas de tren.
 */
export interface CarGroup {
  key: string;
  /** Oferta de referencia (la más barata): de ahí salen las especificaciones. */
  cheapest: CarOffer;
  /** Planes del vehículo, del más barato al más caro. */
  rates: CarOffer[];
}

/** Identidad del vehículo: mismo modelo, compañía, categoría y oficina. */
function carKey(offer: CarOffer): string {
  return [
    offer.carModel ?? '',
    offer.company ?? '',
    offer.sippCode ?? '',
    offer.pickUpLocation ?? '',
    offer.dropOffLocation ?? '',
    offer.officeCode ?? '',
  ].join('|');
}

export function groupCarOffers(offers: CarOffer[]): CarGroup[] {
  const groups = new Map<string, CarOffer[]>();
  for (const offer of offers) {
    const key = carKey(offer);
    const existing = groups.get(key);
    if (existing) existing.push(offer);
    else groups.set(key, [offer]);
  }

  return [...groups.entries()].map(([key, rates]) => {
    const sorted = [...rates].sort(
      (a, b) => a.pricing.final.amountMinor - b.pricing.final.amountMinor,
    );
    return { key, cheapest: sorted[0], rates: sorted };
  });
}

/** Carrocería derivada del SIPP (2º carácter) — define el ícono y el filtro. */
export type CarBody = 'car' | 'suv' | 'van' | 'pickup' | 'convertible' | 'wagon';

/**
 * ACRISS/SIPP: el 2º carácter es el tipo de carrocería. Solo se mapea lo que
 * cambia la lectura de la card; el resto cae en `car`.
 */
const SIPP_BODY: Record<string, CarBody> = {
  V: 'van',
  J: 'suv',
  F: 'suv',
  X: 'suv',
  P: 'pickup',
  Q: 'pickup',
  T: 'convertible',
  W: 'wagon',
  M: 'van',
};

export function carBody(offer: CarOffer): CarBody {
  const sipp = (offer.sippCode ?? '').toUpperCase();
  const fromSipp = SIPP_BODY[sipp.charAt(1)];
  if (fromSipp) return fromSipp;
  // Fallback por categoría: el proveedor a veces manda SIPP raros ("Others").
  const category = (offer.category ?? '').toLowerCase();
  if (category.includes('suv')) return 'suv';
  if (category.includes('van')) return 'van';
  if (category.includes('pick')) return 'pickup';
  if (category.includes('convertible')) return 'convertible';
  return 'car';
}

/** Tokens de categoría del proveedor → español (se traduce palabra por palabra). */
const CATEGORY_TOKENS: Record<string, string> = {
  economy: 'Económico',
  compact: 'Compacto',
  intermediate: 'Intermedio',
  standard: 'Estándar',
  fullsize: 'Grande',
  'full size': 'Grande',
  premium: 'Premium',
  luxury: 'Lujo',
  super: 'Súper',
  special: 'Especial',
  small: 'Chico',
  large: 'Grande',
  mini: 'Mini',
  automatic: 'automático',
  manual: 'manual',
  convertible: 'Convertible',
  minivan: 'Minivan',
  maxivan: 'Maxivan',
  van: 'Van',
  suv: 'SUV',
  'pick up': 'Pick up',
  pickup: 'Pick up',
  sport: 'Deportivo',
  cars: '',
  car: '',
  others: 'Otros',
  other: 'Otros',
  wagon: 'Rural',
  electric: 'Eléctrico',
  hybrid: 'Híbrido',
};

/**
 * Categoría legible en el idioma del usuario. En inglés se devuelve tal cual
 * (es el idioma del proveedor); en español se traducen los tokens conocidos.
 */
export function categoryLabel(category: string | undefined, language: string): string {
  if (!category) return '—';
  if ((language || 'es').split('-')[0] !== 'es') return category;

  const translated = category
    .split(/\s+/)
    .map((word) => {
      const key = word.toLowerCase();
      return CATEGORY_TOKENS[key] ?? word;
    })
    .filter(Boolean)
    .join(' ')
    .trim();
  return translated || category;
}

/** "Unlimited Miles" / "Unlimited Km" → texto local; el resto pasa igual. */
export function mileageLabel(value: string | undefined, language: string): string | undefined {
  if (!value) return undefined;
  if ((language || 'es').split('-')[0] !== 'es') return value;
  const lower = value.toLowerCase();
  if (lower.includes('unlimited')) {
    return lower.includes('mile') ? 'Millas ilimitadas' : 'Km ilimitados';
  }
  return value;
}

/** El proveedor manda "Automatic"/"A" o "Manual"/"M". */
export function transmissionLabel(
  value: string | undefined,
  language: string,
): string | undefined {
  if (!value) return undefined;
  const isAutomatic = /^a/i.test(value);
  if ((language || 'es').split('-')[0] !== 'es') {
    return isAutomatic ? 'Automatic' : 'Manual';
  }
  return isAutomatic ? 'Automática' : 'Manual';
}

/** A/C: el proveedor manda "Yes"/"Y"/"No". */
export function hasAirConditioning(value: string | undefined): boolean {
  return /^(y|s)/i.test(value ?? '');
}

/** Los numéricos vienen como STRING y a veces con decimales ("1.5" valijas) o "X". */
export function parseCount(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Días de alquiler (para mostrar "total por N días"), mínimo 1. */
export function rentalDays(pickUpDate: string, dropOffDate: string): number {
  const from = Date.parse(`${pickUpDate}T00:00:00Z`);
  const to = Date.parse(`${dropOffDate}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 1;
  return Math.max(1, Math.round((to - from) / 86_400_000));
}

/** Las tres keys de URL que puede escribir un punto de retiro/devolución. */
const LOCATION_PARAM_SUFFIXES = ['Code', 'Lat', 'Lng'] as const;

/**
 * Los params de URL de UN punto, con las keys del formato NO elegido puestas
 * explícitamente en `undefined` para que se borren.
 *
 * Hace falta porque `url.commit` MERGEA sobre los params actuales
 * (`useSearchUrlState.ts`). La URL canónica escribe un `plc_*` y borra
 * coordenadas o códigos legacy: `carSearchPayloadFromParams` rechaza cualquier
 * mezcla en vez de elegir una referencia por precedencia. Al apagar
 * "devolución distinta" también se borra un `dropOffCode` anterior.
 *
 * Recibe el resultado ya calculado para que un `plc_*` borre coordenadas o
 * códigos que pueda conservar una URL anterior.
 */
export function carLocationUrlParams(
  chosen: Record<string, string | number>,
  prefix: 'pickUp' | 'dropOff',
): Record<string, string | undefined> {
  return Object.fromEntries(
    LOCATION_PARAM_SUFFIXES.map((suffix) => {
      const key = `${prefix}${suffix}`;
      const value = chosen[key];
      return [key, value === undefined ? undefined : String(value)];
    }),
  );
}

/**
 * Cómo buscar con un lugar de PLACES: siempre por código (el `plc_*` tal
 * cual) — nunca coordenadas. Rutero resuelve cualquier `plc_*` por su cuenta,
 * sea ciudad o aeropuerto (smoke test de
 * #2172: Madrid → MAD, 111 autos, con la ciudad — no el aeropuerto — como
 * `plc_*` de entrada).
 */
export function carPlaceSearchParams(
  place: PlaceRef,
  prefix: 'pickUp' | 'dropOff',
): Record<string, string> {
  return { [`${prefix}Code`]: place.id };
}

/** Nombre corto para resultados con nombres geográficos compuestos. */
export function shortLocationName(entry: { name: string }): string {
  const [first] = entry.name.split(',');
  return first?.trim() || entry.name;
}

/**
 * Lugar (`plc_*`) reconstruido desde la URL. Un código anterior o ajeno al
 * contrato canónico no se presenta como lugar.
 *
 * `country.code` sale del `pais` ya guardado en la URL. Es lo único de este `PlaceRef`
 * que se usa para buscar; el resto son PLACEHOLDERS inertes que nunca se
 * renderizan para el valor YA seleccionado (ver `PlaceAutocomplete.tsx`):
 * `kind`/`breadcrumb` quedan con el `name`, y `country.name` con el CÓDIGO
 * de país, porque la URL no guarda el nombre y no vale gastar otro parámetro
 * en algo que no se muestra.
 */
export function carPlaceFromParams(
  params: Record<string, string>,
  prefix: 'pickUp' | 'dropOff',
): PlaceRef | null {
  const id = params[`${prefix}Code`];
  if (!id || !PLACE_ID_PATTERN.test(id)) return null;
  const name = params[`${prefix}Nombre`] ?? id;
  return {
    id,
    name,
    breadcrumb: name,
    kind: 'city',
    ...(params.pais ? { country: { code: params.pais, name: params.pais } } : {}),
  };
}

/**
 * Por qué un lugar canónico elegido no se puede buscar.
 *
 * - `ok`                    — nada que avisar (sin selección, o buscable).
 * - `placesNoCountry`       — el picker siempre manda código
 *                             (`carPlaceSearchParams` nunca es vacío); lo
 *                             que falta es el `country` del lugar elegido.
 */
export type CarLocationUnsearchableReason =
  | 'ok'
  | 'placesNoCountry';

export function carPickUpUnsearchableReason({
  hasSelection,
  hasCountry,
}: {
  hasSelection: boolean;
  hasCountry: boolean;
}): CarLocationUnsearchableReason {
  if (!hasSelection) return 'ok';
  return hasCountry ? 'ok' : 'placesNoCountry';
}

export interface CarFilters {
  companies: string[];
  rateNames: string[];
  bodies: CarBody[];
  automaticOnly: boolean;
  minPassengers: number;
  /** Solo oficinas EN la terminal (el resto exige shuttle desde el aeropuerto). */
  onTerminalOnly: boolean;
}

export const EMPTY_CAR_FILTERS: CarFilters = {
  companies: [],
  rateNames: [],
  bodies: [],
  automaticOnly: false,
  minPassengers: 0,
  onTerminalOnly: false,
};

export function carFiltersForRatePlanComparison(
  filters: CarFilters,
  state: CarRatePlanComparisonState,
): CarFilters {
  return state === 'completed' ? filters : { ...filters, rateNames: [] };
}

/** El proveedor manda "On Terminal" o "Shuttle". */
export function isOnTerminal(offer: CarOffer): boolean {
  return /terminal/i.test(offer.terminalAccess ?? '');
}

export function filterCars(offers: CarOffer[], filters: CarFilters): CarOffer[] {
  return offers.filter((offer) => {
    if (filters.companies.length && !filters.companies.includes(offer.company ?? '')) {
      return false;
    }
    if (filters.rateNames.length && !filters.rateNames.includes(offer.rateName ?? '')) {
      return false;
    }
    if (filters.bodies.length && !filters.bodies.includes(carBody(offer))) {
      return false;
    }
    if (filters.automaticOnly && !/^a/i.test(offer.transmission ?? '')) return false;
    if (filters.onTerminalOnly && !isOnTerminal(offer)) return false;
    if (filters.minPassengers) {
      const pax = parseCount(offer.passengers);
      // Sin dato de pasajeros no se puede afirmar que cumpla el mínimo.
      if (!pax || pax < filters.minPassengers) return false;
    }
    return true;
  });
}

/**
 * Cuántas ofertas quedarían con ESTA opción puesta, respetando el resto de los
 * filtros. Se pisa la faceta propia (no se acumula con lo ya elegido en ella),
 * que es cómo se cuenta en una búsqueda facetada.
 */
export function carFacetCount(
  offers: CarOffer[],
  filters: CarFilters,
  patch: Partial<CarFilters>,
): number {
  return groupCarOffers(filterCars(offers, { ...filters, ...patch })).length;
}

export type CarSort = 'price' | 'passengers' | 'company';

/** Ordena los VEHÍCULOS (por su plan más barato), no las tarifas sueltas. */
export function sortCarGroups(groups: CarGroup[], sort: CarSort): CarGroup[] {
  const byPrice = (a: CarGroup, b: CarGroup) =>
    a.cheapest.pricing.final.amountMinor - b.cheapest.pricing.final.amountMinor;

  return [...groups].sort((a, b) => {
    if (sort === 'passengers') {
      const diff =
        (parseCount(b.cheapest.passengers) ?? 0) -
        (parseCount(a.cheapest.passengers) ?? 0);
      return diff !== 0 ? diff : byPrice(a, b);
    }
    if (sort === 'company') {
      const diff = (a.cheapest.company ?? '').localeCompare(
        b.cheapest.company ?? '',
      );
      return diff !== 0 ? diff : byPrice(a, b);
    }
    return byPrice(a, b);
  });
}

/**
 * Retiro y devolución del pedido para el mapa del recorrido (#2228). Sale de
 * los parámetros COMMITEADOS (los que dispararon la búsqueda), que ya guardan
 * `lat`/`lng` y el nombre del lugar — no del formulario vivo.
 *
 * Un punto solo significa UNA cosa: se retira y se devuelve en el mismo lugar
 * (el mapa lo dibuja como un pin). Por eso, si el pedido SÍ tiene devolución
 * en otro lugar y ese lugar no se puede mapear —los dos puntos no tienen por
 * qué venir en el mismo formato: uno puede ser código de proveedor y el otro
 * coordenadas—, no hay mapa en vez de uno que dice que se devuelve donde se
 * retiró.
 *
 * Sin coordenadas en el retiro tampoco hay mapa.
 */
export function carRouteMapPoints(
  params: Record<string, string>,
): RouteMapPoint[] {
  const point = (prefix: 'pickUp' | 'dropOff'): RouteMapPoint | null => {
    const lat = Number(params[`${prefix}Lat`]);
    const lng = Number(params[`${prefix}Lng`]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const label = params[`${prefix}Nombre`];
    return { lat, lng, ...(label ? { label } : {}) };
  };

  const pickUp = point('pickUp');
  if (!pickUp) return [];

  // El pedido declara devolución aparte con CUALQUIERA de los dos formatos
  // que escribe el formulario (`carLocationUrlParams`).
  const hasOwnDropOff = LOCATION_PARAM_SUFFIXES.some(
    (suffix) => params[`dropOff${suffix}`],
  );
  if (!hasOwnDropOff) return routeMapPoints([pickUp]);

  const dropOff = point('dropOff');
  if (!dropOff) return [];
  return routeMapPoints([pickUp, dropOff]);
}
